import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAlert } from '../components/AlertContext';
import { validarEmail, validarRut, formatearRut, contienePalabrasOfensivas } from '../utils/validaciones';
import Sidebar from '../components/Sidebar';
import Skeleton from '../components/Skeleton';
import Pagination from '../components/Pagination';
import './Dashboard.css'

const Funcionarios = () => {
  const navigate = useNavigate();
  const { toast, confirm } = useAlert();
  const [funcionarios, setFuncionarios] = useState([]);
  const [user, setUser] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroCargo, setFiltroCargo] = useState('');
  const [filtroArea, setFiltroArea] = useState('');
  const [profanityList, setProfanityList] = useState('');
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activeTab = searchParams.get('tab') || 'activos';
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', apellido: '', email: '', rut: '', cargo: '', departamento: '' });
  const [areasList, setAreasList] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedFuncionario, setSelectedFuncionario] = useState(null);
  const [funcionarioEquipos, setFuncionarioEquipos] = useState([]);
  const [isEquiposLoading, setIsEquiposLoading] = useState(false);

  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const apiBase = isLocal ? `http://${window.location.hostname}:3010` : `${window.location.protocol}//${window.location.hostname}`;


  const fetchFuncionarios = async () => {
    setIsLoading(true);
    try {
      const resp = await fetch(`${apiBase}/api/funcionarios`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setFuncionarios(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProfanityList = async () => {
    try {
      const resp = await fetch(`${apiBase}/api/config/profanity`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setProfanityList(data.words);
      }
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
    
    fetchFuncionarios();
    fetchProfanityList();
    fetchAreas();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filtroCargo, filtroArea, activeTab]);

  const fetchAreas = async () => {
    try {
      const resp = await fetch(`${apiBase}/api/areas`, { credentials: 'include' });
      if (resp.ok) setAreasList(await resp.json());
    } catch (err) { console.error('Error cargando áreas:', err); }
  };

  const handleSave = async () => {
    const errors = {};
    if (!formData.nombre) errors.nombre = 'El nombre es requerido.';
    if (!formData.apellido) errors.apellido = 'El apellido es requerido.';
    if (!validarRut(formData.rut)) errors.rut = 'RUT inválido (Módulo 11).';
    if (!formData.cargo) errors.cargo = 'El cargo es requerido.';
    if (!validarEmail(formData.email)) errors.email = 'Ingresa un email válido.';
    if (!formData.departamento) errors.departamento = 'El departamento es obligatorio para el enrutamiento de solicitudes.';

    const offensive = contienePalabrasOfensivas(`${formData.nombre} ${formData.apellido} ${formData.cargo}`, profanityList);
    if (offensive) {
      errors.nombre = `Referencia inapropiada detectada ("${offensive}").`;
    }

    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});
    const nombreCompleto = `${formData.nombre.trim()} ${formData.apellido.trim()}`;
    const url = isEditMode ? `${apiBase}/api/funcionarios/update/${editingId}` : `${apiBase}/api/funcionarios`;
    const method = 'POST';

    try {
      const resp = await fetch(url, { credentials: 'include', 
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: nombreCompleto,
          email: formData.email,
          rut: formData.rut,
          cargo: formData.cargo,
          departamento: formData.departamento
        })
      });

      if (resp.ok) {
        setIsModalOpen(false);
        setIsEditMode(false);
        setEditingId(null);
        setFormData({ nombre: '', apellido: '', email: '', rut: '', cargo: '', departamento: '' });
        fetchFuncionarios();
        toast.success('Funcionario guardado correctamente.');
      } else {
        const errorData = await resp.json();
        toast.error(`Error: ${errorData.error || 'No se pudo guardar'}`);
      }
    } catch (err) {
      toast.error('Error de red al guardar.');
    }
  };

  const handleOpenHistory = async (funcionario) => {
    setSelectedFuncionario(funcionario);
    setHistoryModalOpen(true);
    setIsEquiposLoading(true);
    try {
      const resp = await fetch(`${apiBase}/api/funcionarios/${funcionario.id}/equipos`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setFuncionarioEquipos(data);
      }
    } catch (err) {
      console.error(err);
      toast.error('Error obteniendo historial de equipos');
    } finally {
      setIsEquiposLoading(false);
    }
  };

  const handleAprobar = async (funcionario) => {
    confirm(
      "Aprobar Funcionario",
      `¿Seguro que deseas aprobar a ${funcionario.nombre} para que pueda utilizar el sistema?`,
      async () => {
        try {
          const resp = await fetch(`${apiBase}/api/funcionarios/aprobar/${funcionario.id}`, { credentials: 'include',  method: 'POST' });
          if (resp.ok) {
            toast.success('Funcionario aprobado con éxito.');
            fetchFuncionarios();
          } else {
            toast.error("Error al aprobar funcionario.");
          }
        } catch (err) {
          toast.error("Error de conexión al aprobar.");
        }
      },
      { confirmText: 'Aprobar', type: 'info' }
    );
  };

  const handleEdit = (f) => {
    const parts = f.nombre.split(' ');
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ') || '';

    setFormData({
      nombre: firstName,
      apellido: lastName,
      email: f.email,
      rut: f.rut || '',
      cargo: f.cargo || '',
      departamento: f.departamento || ''
    });
    setEditingId(f.id);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDesvincular = async (id, nombre, estado_actual, cantidad_equipos = 0) => {
    const isDesvinculando = estado_actual !== 'Desvinculado';
    
    if (isDesvinculando && cantidad_equipos > 0) {
      toast.error(`No se puede desvincular a ${nombre} porque aún tiene ${cantidad_equipos} equipo(s) a su disposición.`);
      return;
    }

    confirm(
      isDesvinculando ? "Desvincular Funcionario" : "Reactivar Funcionario",
      `¿Seguro que deseas ${isDesvinculando ? 'desvincular' : 'reactivar'} a: ${nombre}?`,
      async () => {
        try {
          const resp = await fetch(`${apiBase}/api/funcionarios/${id}/estado`, { credentials: 'include',  
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ estado: isDesvinculando ? 'Desvinculado' : 'Activo' })
          });
          if (resp.ok) {
            toast.success(`Funcionario ${isDesvinculando ? 'desvinculado' : 'reactivado'} con éxito.`);
            fetchFuncionarios();
          } else {
            toast.error('No se pudo modificar el estado.');
          }
        } catch (err) {
          toast.error('Error contactando al servidor.');
        }
      },
      { confirmText: isDesvinculando ? 'Desvincular' : 'Reactivar', type: isDesvinculando ? 'danger' : 'info' }
    );
  };

  const uniqueCargos = Array.from(new Set(funcionarios.map(f => f.cargo).filter(Boolean))).sort();

  const filteredFuncionarios = funcionarios.filter(f => {
    const term = searchTerm.toLowerCase();
    const matchSearch = f.nombre.toLowerCase().includes(term) ||
      (f.equipos_asignados || "").toLowerCase().includes(term);
    const matchCargo = !filtroCargo || f.cargo === filtroCargo;
    const matchArea = !filtroArea || f.departamento === filtroArea;
    const isPending = f.estado_funcionario === 'Pendiente';
    const isDesvinculado = f.estado_funcionario === 'Desvinculado';
    
    if (activeTab === 'pendientes') {
      return matchSearch && matchCargo && matchArea && isPending;
    }
    if (activeTab === 'desvinculados') {
      return matchSearch && matchCargo && matchArea && isDesvinculado;
    }
    return matchSearch && matchCargo && matchArea && !isPending && !isDesvinculado;
  });

  const totalPages = Math.ceil(filteredFuncionarios.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredFuncionarios.slice(indexOfFirstItem, indexOfLastItem);

  const pendingCount = funcionarios.filter(f => f.estado_funcionario === 'Pendiente').length;

  return (
    <div className="dashboard-layout">
      <Sidebar />

      <main className="main-content">
        <header className="content-header">
          <div className="header-title">
            <h1>Gestión de Funcionarios</h1>
          </div>
          <div className="header-actions">
            <Link to="/perfil" className="user-profile">
              <span>{JSON.parse(localStorage.getItem('user') || '{}').nombre || 'Mi Perfil'}</span>
              <div className="avatar">
                {JSON.parse(localStorage.getItem('user') || '{}').nombre?.charAt(0).toUpperCase() || 'P'}
              </div>
            </Link>
          </div>
        </header>

        <section className="datatable-section" style={{ marginTop: '30px' }}>
          <div className="datatable-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '25px', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', paddingTop: '5px' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: activeTab === 'pendientes' ? 'linear-gradient(135deg, var(--p-amber), #f59e0b)' : activeTab === 'desvinculados' ? 'linear-gradient(135deg, var(--p-rose), #ef4444)' : 'linear-gradient(135deg, var(--p-indigo), var(--p-blue))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '16px' }}>
                <i className={`fa-solid ${activeTab === 'pendientes' ? 'fa-user-clock' : activeTab === 'desvinculados' ? 'fa-user-slash' : 'fa-users'}`}></i>
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px' }}>
                  {activeTab === 'pendientes' ? 'Registros por Revisar' : activeTab === 'desvinculados' ? 'Personal Desvinculado' : 'Personal Municipal'}
                </h2>
                <p style={{ margin: 0, fontSize: '11px', color: 'var(--p-slate)' }}>
                  {activeTab === 'pendientes' ? 'Funcionarios pendientes de aprobación' : activeTab === 'desvinculados' ? 'Funcionarios inactivos del sistema' : 'Funcionarios activos y sus asignaciones'}
                </p>
              </div>
              {filteredFuncionarios.length > 0 && <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--p-blue)', background: 'var(--sidebar-accent)', padding: '4px 12px', borderRadius: '12px', border: '1px solid var(--glass-border)' }}>{filteredFuncionarios.length}</span>}
            </div>
            <div className="header-actions" style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="search-bar" style={{ margin: 0 }}>
                <i className="fa-solid fa-magnifying-glass search-icon" style={{ position: 'absolute', marginLeft: '15px', marginTop: '16px', color: 'var(--p-slate)' }}></i>
                <input
                  type="text"
                  placeholder="Buscar funcionario..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '40px', width: '250px', height: '45px' }}
                />
              </div>

              <select
                className="select-premium"
                value={filtroCargo}
                onChange={(e) => setFiltroCargo(e.target.value)}
                style={{ width: '180px', height: '45px', margin: 0 }}
              >
                <option value="">Todos los Cargos</option>
                {uniqueCargos.map(cargo => (
                  <option key={cargo} value={cargo}>{cargo}</option>
                ))}
              </select>

              <select
                className="select-premium"
                value={filtroArea}
                onChange={(e) => setFiltroArea(e.target.value)}
                style={{ width: '180px', height: '45px', margin: 0 }}
              >
                <option value="">Todas las Áreas</option>
                {areasList.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
              {activeTab === 'activos' && (
                <button className="btn-primary" onClick={() => { setIsEditMode(false); setEditingId(null); setFormData({ nombre: '', apellido: '', email: '', rut: '', cargo: '', departamento: '' }); setIsModalOpen(true); }} style={{ height: '45px', margin: 0 }}><i className="fa-solid fa-user-plus"></i> Añadir Funcionario</button>
              )}
            </div>
          </div>

          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th><i className="fa-solid fa-user" style={{ marginRight: '6px', opacity: 0.6 }}></i>Funcionario</th>
                  <th><i className="fa-solid fa-id-card" style={{ marginRight: '6px', opacity: 0.6 }}></i>RUT</th>
                  <th><i className="fa-solid fa-briefcase" style={{ marginRight: '6px', opacity: 0.6 }}></i>Cargo</th>
                  <th><i className="fa-solid fa-building" style={{ marginRight: '6px', opacity: 0.6 }}></i>Área</th>
                  <th><i className="fa-solid fa-envelope" style={{ marginRight: '6px', opacity: 0.6 }}></i>Contacto</th>
                  <th style={{ textAlign: 'center' }}><i className="fa-solid fa-laptop" style={{ marginRight: '6px', opacity: 0.6 }}></i>Equipos</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="skeleton-row-tr">
                      <td><Skeleton width="150px" /></td>
                      <td><Skeleton width="100px" /></td>
                      <td><Skeleton width="120px" /></td>
                      <td><Skeleton width="100px" /></td>
                      <td><Skeleton width="180px" /></td>
                      <td><Skeleton width="40px" /></td>
                      <td><Skeleton width="100px" /></td>
                    </tr>
                  ))
                ) : currentItems.length > 0 ? (
                  currentItems.map((f, i) => (
                    <tr key={f.id || i}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: f.estado_funcionario === 'Pendiente' ? 'linear-gradient(135deg, var(--p-amber), #f59e0b)' : f.estado_funcionario === 'Desvinculado' ? 'var(--sidebar-accent)' : 'linear-gradient(135deg, var(--p-indigo), var(--p-blue))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: f.estado_funcionario === 'Desvinculado' ? 'var(--p-slate)' : 'white', fontSize: '12px', fontWeight: '700', flexShrink: 0 }}>{(f.nombre || '?').charAt(0)}</div>
                          <div>
                            <strong style={{ fontSize: '13px' }}>{f.nombre}</strong>
                            {f.estado_funcionario === 'Pendiente' && (
                              <span style={{ marginLeft: '6px', background: 'rgba(245, 158, 11, 0.1)', color: 'var(--p-amber)', border: '1px solid rgba(245, 158, 11, 0.2)', padding: '2px 8px', borderRadius: '8px', fontSize: '9px', fontWeight: '700' }}>
                                Pendiente
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td><span style={{ fontSize: '12px', color: 'var(--p-slate)', fontFamily: 'var(--font-mono)' }}>{f.rut || 'N/A'}</span></td>
                      <td style={{ fontSize: '13px', fontWeight: '600', color: 'var(--p-navy)' }}>{f.cargo || 'Funcionario'}</td>
                      <td style={{ fontSize: '12px', color: 'var(--p-slate)' }}>
                        {f.departamento ? (
                          <span style={{ background: 'var(--sidebar-accent)', color: 'var(--p-navy)', border: '1px solid var(--glass-border)', padding: '3px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' }}>
                            {f.departamento}
                          </span>
                        ) : <span style={{ color: 'var(--p-slate)', fontSize: '11px', fontStyle: 'italic' }}>Sin área</span>}
                      </td>
                      <td><span style={{ fontSize: '12px', color: 'var(--p-slate)' }}>{f.email}</span></td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="badge" style={{ background: f.cantidad_equipos > 0 ? 'rgba(56, 189, 248, 0.1)' : 'var(--card-bg)', color: f.cantidad_equipos > 0 ? 'var(--p-blue)' : 'var(--p-slate)', border: f.cantidad_equipos > 0 ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid var(--glass-border)' }}>
                          {f.cantidad_equipos}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          {f.estado_funcionario === 'Pendiente' ? (
                            (user.rol === 'superadmin' || user.permisos?.includes('MODULO_ADMIN')) ? (
                              <>
                                <button className="action-button-glass" onClick={() => handleAprobar(f)} title="Aprobar Funcionario" style={{ color: 'var(--p-emerald)' }}>
                                  <i className="fa-solid fa-check-circle"></i>
                                </button>
                                <button className="action-button-glass action-delete" onClick={() => handleDesvincular(f.id, f.nombre, f.estado_funcionario, f.cantidad_equipos)} title="Desvincular Funcionario">
                                  <i className="fa-solid fa-user-slash"></i>
                                </button>
                              </>
                            ) : null
                          ) : (
                            <>
                              <button className="action-button-glass" onClick={() => handleEdit(f)} title="Editar Funcionario">
                                <i className="fa-solid fa-pen-to-square"></i>
                              </button>
                              <button className="action-button-glass" onClick={() => handleOpenHistory(f)} title="Ver Historial">
                                <i className="fa-solid fa-user-clock"></i>
                              </button>
                              <button className="action-button-glass action-delete" onClick={() => handleDesvincular(f.id, f.nombre, f.estado_funcionario, f.cantidad_equipos)} title={f.estado_funcionario === 'Desvinculado' ? 'Reactivar Funcionario' : 'Desvincular Funcionario'} style={f.estado_funcionario === 'Desvinculado' ? {color: 'var(--p-emerald)'} : {}}>
                                <i className={`fa-solid ${f.estado_funcionario === 'Desvinculado' ? 'fa-user-check' : 'fa-user-slash'}`}></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '60px', color: 'var(--p-slate)' }}>
                      <i className="fa-solid fa-users-slash" style={{ fontSize: '28px', display: 'block', marginBottom: '12px', opacity: 0.4 }}></i>
                      {activeTab === 'pendientes' ? 'No hay registros pendientes de aprobación.' : activeTab === 'desvinculados' ? 'No hay funcionarios desvinculados.' : 'No se encontraron funcionarios.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={setCurrentPage} 
          />
        </section>
      </main>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content premium-modal" style={{ maxWidth: '680px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '25px', paddingBottom: '18px', borderBottom: '1px solid var(--glass-border)' }}>
              <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: isEditMode ? 'linear-gradient(135deg, var(--p-amber), #f59e0b)' : 'linear-gradient(135deg, var(--p-indigo), var(--p-blue))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '18px', flexShrink: 0 }}>
                <i className={`fa-solid ${isEditMode ? 'fa-user-pen' : 'fa-user-plus'}`}></i>
              </div>
              <div>
                <h2 style={{ color: 'var(--p-navy)', margin: 0, fontSize: '17px' }}>{isEditMode ? 'Editar Funcionario' : 'Registrar Funcionario'}</h2>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--p-slate)' }}>{isEditMode ? 'Modificar datos del funcionario' : 'Completa los datos del nuevo funcionario'}</p>
              </div>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="crud-form">
              {/* Fila 1: Nombre y Apellido */}
              <div className="form-row">
                <div className="premium-field">
                  <label><i className="fa-solid fa-user" style={{ color: 'var(--p-indigo)' }}></i> Nombre(s)</label>
                  <input type="text" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} placeholder="Ej: Daniel" style={formErrors.nombre ? { borderColor: 'var(--p-rose)' } : {}} />
                  {formErrors.nombre && <small style={{ color: 'var(--p-rose)' }}>{formErrors.nombre}</small>}
                </div>
                <div className="premium-field">
                  <label><i className="fa-solid fa-user-tag" style={{ color: 'var(--p-indigo)' }}></i> Apellido(s)</label>
                  <input type="text" value={formData.apellido} onChange={(e) => setFormData({ ...formData, apellido: e.target.value })} placeholder="Ej: Sánchez" style={formErrors.apellido ? { borderColor: 'var(--p-rose)' } : {}} />
                  {formErrors.apellido && <small style={{ color: 'var(--p-rose)' }}>{formErrors.apellido}</small>}
                </div>
              </div>
              {/* Fila 2: RUT y Cargo */}
              <div className="form-row">
                <div className="premium-field">
                  <label><i className="fa-solid fa-id-card" style={{ color: 'var(--p-blue)' }}></i> RUT</label>
                  <input
                    type="text"
                    value={formData.rut}
                    onChange={(e) => setFormData({ ...formData, rut: formatearRut(e.target.value) })}
                    placeholder="12.345.678-9"
                    style={formErrors.rut ? { borderColor: 'var(--p-rose)' } : {}}
                  />
                  {formErrors.rut && <small style={{ color: 'var(--p-rose)' }}>{formErrors.rut}</small>}
                </div>
                <div className="premium-field">
                  <label><i className="fa-solid fa-briefcase" style={{ color: 'var(--p-amber)' }}></i> Cargo</label>
                  <input
                    type="text"
                    value={formData.cargo}
                    onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                    placeholder="Ej: Analista de Sistemas"
                    style={formErrors.cargo ? { borderColor: 'var(--p-rose)' } : {}}
                  />
                  {formErrors.cargo && <small style={{ color: 'var(--p-rose)' }}>{formErrors.cargo}</small>}
                </div>
              </div>
              {/* Fila 3: Área y Email */}
              <div className="form-row">
                <div className="premium-field">
                  <label><i className="fa-solid fa-building" style={{ color: 'var(--p-emerald)' }}></i> Área / Departamento</label>
                  <select
                    className="select-premium"
                    value={formData.departamento}
                    onChange={(e) => setFormData({ ...formData, departamento: e.target.value })}
                    style={{ width: '100%', height: '50px', ...(formErrors.departamento ? { borderColor: 'var(--p-rose)' } : {}) }}
                  >
                    <option value="">— Seleccionar área —</option>
                    {areasList.map(area => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                  </select>
                  {formErrors.departamento && <small style={{ color: 'var(--p-rose)', display: 'block', marginTop: '4px' }}>{formErrors.departamento}</small>}
                </div>
                <div className="premium-field">
                  <label><i className="fa-solid fa-envelope" style={{ color: 'var(--p-rose)' }}></i> Email de Contacto</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="funcionario@municipalidad.cl" style={formErrors.email ? { borderColor: 'var(--p-rose)' } : {}} />
                  {formErrors.email && <small style={{ color: 'var(--p-rose)' }}>{formErrors.email}</small>}
                </div>
              </div>
              <div className="modal-footer-actions">
                <button type="button" className="btn-secondary" onClick={() => { setIsModalOpen(false); setIsEditMode(false); setEditingId(null); setFormErrors({}); }}><i className="fa-solid fa-xmark" style={{ marginRight: '6px' }}></i> Cancelar</button>
                <button type="submit" className="btn-primary"><i className="fa-solid fa-save" style={{ marginRight: '6px' }}></i> {isEditMode ? 'Guardar Cambios' : 'Registrar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {historyModalOpen && selectedFuncionario && (
        <div className="modal-overlay">
          <div className="modal-content premium-modal x-large" style={{ maxWidth: '700px' }}>
            {/* Header con avatar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '20px', borderBottom: '1px solid var(--glass-border)', marginBottom: '25px' }}>
              <div style={{ width: '50px', height: '50px', borderRadius: '14px', background: 'linear-gradient(135deg, var(--p-indigo), var(--p-blue))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '20px', fontWeight: '700', flexShrink: 0 }}>{selectedFuncionario.nombre.charAt(0)}</div>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0, color: 'var(--p-navy)', fontSize: '18px' }}>{selectedFuncionario.nombre}</h2>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--p-slate)' }}>{selectedFuncionario.cargo || 'Funcionario Municipal'}</p>
              </div>
              <span className="badge status-en-servicio" style={{ fontSize: '10px' }}>Activo</span>
            </div>

            <div style={{ padding: '0 5px' }}>
              {/* Info cards */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '25px' }}>
                <div style={{ padding: '18px', background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--glass-border)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--p-slate)', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                    <i className="fa-solid fa-id-card" style={{ color: 'var(--p-blue)', fontSize: '10px' }}></i> Datos de Contacto
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--p-navy)', marginBottom: '4px' }}><i className="fa-solid fa-fingerprint" style={{ color: 'var(--p-slate)', marginRight: '6px', fontSize: '10px' }}></i> <span style={{ fontFamily: 'var(--font-mono)' }}>{selectedFuncionario.rut || 'No registrado'}</span></div>
                  <div style={{ fontSize: '13px', color: 'var(--p-navy)' }}><i className="fa-solid fa-envelope" style={{ color: 'var(--p-slate)', marginRight: '6px', fontSize: '10px' }}></i> {selectedFuncionario.email}</div>
                </div>
                <div style={{ padding: '18px', background: 'var(--sidebar-accent)', borderRadius: '14px', border: '1px solid var(--glass-border)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--p-blue)', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                    <i className="fa-solid fa-chart-pie" style={{ fontSize: '10px' }}></i> Resumen de Equipos
                  </div>
                  <div style={{ fontSize: '26px', fontWeight: '800', color: 'var(--p-navy)' }}>{funcionarioEquipos.length}</div>
                  <div style={{ fontSize: '11px', color: 'var(--p-slate)' }}>
                    {Array.from(new Set(funcionarioEquipos.map(e => e.categoria))).slice(0, 3).join(', ') || 'Sin equipos asignados'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <i className="fa-solid fa-laptop" style={{ color: 'var(--p-indigo)', fontSize: '14px' }}></i>
                <h3 style={{ fontSize: '14px', margin: 0, color: 'var(--p-navy)' }}>Inventario bajo su responsabilidad</h3>
                {funcionarioEquipos.length > 0 && <span style={{ fontSize: '10px', color: 'var(--p-blue)', background: 'var(--sidebar-accent)', padding: '3px 10px', borderRadius: '12px', border: '1px solid var(--glass-border)', fontWeight: '700' }}>{funcionarioEquipos.length}</span>}
              </div>

              {isEquiposLoading ? (
                <div style={{ textAlign: 'center', padding: '30px' }}>
                  <div style={{ border: '3px solid var(--glass-border)', borderTop: '3px solid var(--p-blue)', borderRadius: '50%', width: '24px', height: '24px', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
                </div>
              ) : funcionarioEquipos.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {funcionarioEquipos.map((eq, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--glass-border)' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--sidebar-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--p-blue)', fontSize: '14px', flexShrink: 0, border: '1px solid var(--glass-border)' }}>
                        <i className="fa-solid fa-laptop"></i>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--p-navy)' }}>{eq.modelo}</div>
                        <div style={{ fontSize: '11px', color: 'var(--p-slate)' }}><span style={{ fontFamily: 'var(--font-mono)' }}>{eq.codigo}</span> · {eq.categoria}</div>
                      </div>
                      <span className={`badge status-${(eq.estado || 'operativo').toLowerCase().replace(' ', '-')}`} style={{ fontSize: '10px' }}>{eq.estado}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', background: 'var(--card-bg)', borderRadius: '14px', border: '1px dashed var(--glass-border)' }}>
                  <i className="fa-solid fa-box-open" style={{ fontSize: '24px', display: 'block', marginBottom: '10px', opacity: 0.4 }}></i>
                  <p style={{ color: 'var(--p-slate)', fontSize: '13px', margin: 0 }}>No tiene equipos asignados actualmente.</p>
                </div>
              )}

              <div style={{ marginTop: '22px', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(99, 102, 241, 0.05)', borderRadius: '8px', border: '1px solid var(--glass-border)' }}>
                <i className="fa-solid fa-lightbulb" style={{ color: 'var(--p-amber)', fontSize: '12px', flexShrink: 0 }}></i>
                <p style={{ fontSize: '11px', color: 'var(--p-slate)', margin: 0 }}>Vista de <strong>solo lectura</strong>. Usa el panel de Equipos para reasignar o dar de baja.</p>
              </div>
            </div>

            <div className="modal-footer-actions" style={{ marginTop: '20px' }}>
              <button className="btn-secondary" onClick={() => setHistoryModalOpen(false)}><i className="fa-solid fa-xmark" style={{ marginRight: '6px' }}></i> Cerrar Ficha</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Funcionarios;
